import { useEffect, useRef, useState } from "react";import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GRADE_LEVELS, COURSES, labelForGrade } from "@/lib/constants";
import { Send, Sparkles, Loader2, AlertCircle, RotateCw, Copy, Check, HelpCircle, GraduationCap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AiDisclosure } from "@/components/AiDisclosure";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import { useServerVoiceInput } from "@/hooks/useServerVoiceInput";
import { useVoicePlayback } from "@/hooks/useVoicePlayback";
import { MicButton } from "@/components/voice/MicButton";
import { VoicePreview } from "@/components/voice/VoicePreview";
import { SpeakButton } from "@/components/voice/SpeakButton";
import { VoiceConsentCard } from "@/components/voice/VoiceConsentCard";
import { VoiceServerConsentCard } from "@/components/voice/VoiceServerConsentCard";

const VOICE_CONSENT_KEY = "sz_voice_consent_v1";
const VOICE_SERVER_CONSENT_KEY = "sz_voice_server_consent_v1";

const STREAMING_MSG_PREFIX = "streaming-";

/**
 * Split an assistant message into the plain body and the three optional
 * pedagogical insight sections. Sections are recognised via literal Markdown
 * H2 headings ("## Neden takıldın", "## Ön koşul", "## Sıradaki adım") emitted
 * by the TeacherEngine STRUCTURED_INSIGHT_POLICY. Everything else is treated
 * as the answer body.
 */
function splitAssistantContent(content) {
  if (!content) return { body: "", insights: null };
  const re = /^##\s+(Neden takıldın|Ön koşul|Sıradaki adım)\s*$/im;
  const idx = content.search(re);
  if (idx === -1) return { body: content, insights: null };
  const body = content.slice(0, idx).trim();
  const rest = content.slice(idx);
  const parts = rest.split(/^##\s+/m).map((p) => p.trim()).filter(Boolean);
  const insights = { why: "", prereq: "", next: "" };
  for (const p of parts) {
    const nl = p.indexOf("\n");
    const heading = (nl === -1 ? p : p.slice(0, nl)).trim().toLowerCase();
    const text = nl === -1 ? "" : p.slice(nl + 1).trim();
    if (heading.startsWith("neden takıldın")) insights.why = text;
    else if (heading.startsWith("ön koşul")) insights.prereq = text;
    else if (heading.startsWith("sıradaki adım")) insights.next = text;
  }
  if (!insights.why && !insights.prereq && !insights.next) {
    return { body: content, insights: null };
  }
  return { body, insights };
}

async function readSseStream(response, handlers) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const dataLine = rawEvent
        .split("\n")
        .filter((l) => l.startsWith("data: "))
        .map((l) => l.slice(6))
        .join("");
      if (!dataLine) continue;
      let evt;
      try { evt = JSON.parse(dataLine); } catch (_) { continue; }
      handlers[evt.type]?.(evt);
    }
  }
}

export default function Chat() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const [grade, setGrade] = useState(user?.grade_level || "");
  const [course, setCourse] = useState(user?.selected_course || "");
  const [convId, setConvId] = useState(params.get("id") || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const streamIdRef = useRef(null);
  const inputRef = useRef(null);

  // Voice I/O — deliberately layered around the existing chat pipeline.
  // The mic writes into `input` via the existing setInput setter; the
  // send path (send / sendWith / runPostSSE) is not modified.
  //
  // Two independent capture paths are exposed:
  //   nativeVoice — window.SpeechRecognition, on-device (C1).
  //   serverVoice — MediaRecorder + POST /api/voice/transcribe, only
  //                 used as a fallback for browsers where nativeVoice
  //                 is unsupported. Requires its OWN one-time consent
  //                 because the audio actually leaves the device.
  const nativeVoice = useVoiceInput({ lang: "tr-TR" });
  const serverVoice = useServerVoiceInput();
  const playback = useVoicePlayback();

  // Pick the ACTIVE capture path once per render. Native has strict
  // priority; server fallback only becomes active when native is
  // unsupported AND server MediaRecorder is available.
  const useServerFallback = !nativeVoice.supported && serverVoice.supported;
  const voice = useServerFallback ? serverVoice : nativeVoice;

  const [voiceConsent, setVoiceConsent] = useState(() => {
    try { return localStorage.getItem(VOICE_CONSENT_KEY) || ""; } catch { return ""; }
  });
  const [voiceServerConsent, setVoiceServerConsent] = useState(() => {
    try { return localStorage.getItem(VOICE_SERVER_CONSENT_KEY) || ""; } catch { return ""; }
  });
  const [voiceConsentOpen, setVoiceConsentOpen] = useState(false);
  const [voiceServerConsentOpen, setVoiceServerConsentOpen] = useState(false);
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);
  // When we create a new conversation locally via SSE meta, we've already got
  // the messages in state. Setting the URL id would otherwise re-trigger the
  // params useEffect and refetch, racing with the stream. Skip once.
  const skipNextFetchIdRef = useRef(null);

  useEffect(() => {
    const id = params.get("id");
    const shouldSkip = skipNextFetchIdRef.current === id;
    skipNextFetchIdRef.current = null;

    setConvId(id || null);
    if (!id) {
      setMessages([]);
      return;
    }
    if (shouldSkip) return;
    setLoadingConv(true);
    api.get(`/chat/conversations/${id}`)
      .then((r) => {
        setMessages(r.data.messages || []);
        if (r.data.grade_level) setGrade(r.data.grade_level);
        if (r.data.course) setCourse(r.data.course);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoadingConv(false));
  }, [params]);

  // If arriving with ?prompt= (and no ?id=), auto-start a new chat with that prompt.
  // Also honors ?grade= and ?course= to preselect selections coming from Panel topics.
  const autoSentRef = useRef(false);
  useEffect(() => {
    const promptText = params.get("prompt");
    const gradeQ = params.get("grade");
    const courseQ = params.get("course");
    if (params.get("id") || !promptText || autoSentRef.current) return;
    autoSentRef.current = true;
    if (gradeQ) setGrade(gradeQ);
    if (courseQ) setCourse(courseQ);
    // Clear query params (keep URL clean) and trigger send once state has propagated
    setParams({}, { replace: true });
    setInput(promptText);
    // wait a tick for grade/course state to be applied
    setTimeout(() => {
      const g = gradeQ || grade;
      const c = courseQ || course;
      if (g && c) {
        sendWith(promptText, g, c);
      }
    }, 50);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming]);

  const runPostSSE = async (url, body, { onMeta } = {}) => {
    const token = localStorage.getItem("sz_token");
    const controller = new AbortController();
    abortRef.current = controller;
    const placeholderId = streamIdRef.current;

    const resp = await fetch(url, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!resp.ok || !resp.body) {
      let errText = "İstek başarısız oldu.";
      try {
        const j = await resp.json();
        errText = formatApiError({ response: { data: j } });
      } catch (_) {
        /* ignore JSON parse error */
      }
      throw new Error(errText);
    }

    let acc = "";
    let finalAssistant = null;
    let streamErr = null;
    await readSseStream(resp, {
      meta: (e) => onMeta?.(e),
      delta: (e) => {
        acc += e.content || "";
        setMessages((m) => m.map((x) => (x.id === placeholderId ? { ...x, content: acc } : x)));
      },
      done: (e) => { finalAssistant = e.assistant_message; },
      error: (e) => { streamErr = e.message || "Bilinmeyen hata"; },
    });
    return { acc, finalAssistant, streamErr, placeholderId };
  };

  const send = async () => {
    if (streaming) return; // prevent duplicate submissions
    const text = input.trim();
    if (!text) return;
    if (!grade || !course) {
      toast.error("Önce sınıf ve ders seç");
      return;
    }
    await sendWith(text, grade, course);
  };

  const sendWith = async (text, gradeVal, courseVal) => {
    setStreaming(true);
    setInput("");

    const placeholderId = `${STREAMING_MSG_PREFIX}${Date.now()}`;
    streamIdRef.current = placeholderId;

    const optimistic = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    const placeholder = {
      id: placeholderId,
      role: "assistant",
      content: "",
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic, placeholder]);

    try {
      let realUserMsg = null;
      const { acc, finalAssistant, streamErr } = await runPostSSE(
        `${API_BASE}/chat/stream`,
        { conversation_id: convId, message: text, grade_level: gradeVal, course: courseVal },
        {
          onMeta: (e) => {
            realUserMsg = e.user_message;
            if (!convId && e.conversation_id) {
              skipNextFetchIdRef.current = e.conversation_id;
              setConvId(e.conversation_id);
              setParams({ id: e.conversation_id }, { replace: true });
            }
          },
        }
      );

      setMessages((m) => {
        const kept = m.filter((x) => x.id !== placeholderId && x.id !== optimistic.id);
        const seen = new Set(kept.map((x) => x.id));
        const final = [...kept];
        const userToPush = realUserMsg || optimistic;
        if (!seen.has(userToPush.id)) { final.push(userToPush); seen.add(userToPush.id); }
        if (finalAssistant) {
          if (!seen.has(finalAssistant.id)) final.push(finalAssistant);
        } else if (acc) {
          final.push({
            id: `local-${Date.now()}`,
            role: "assistant",
            content: acc,
            created_at: new Date().toISOString(),
          });
        }
        return final;
      });
      if (streamErr) toast.error(streamErr);
    } catch (e) {
      if (e.name === "AbortError") {
        setMessages((m) => m.filter((x) => x.id !== placeholderId));
      } else {
        setMessages((m) => m.filter((x) => x.id !== placeholderId && x.id !== optimistic.id));
        toast.error(e.message || "Bağlantı hatası");
      }
    } finally {
      setStreaming(false);
      streamIdRef.current = null;
      abortRef.current = null;
      // Return focus so user can immediately continue typing (ChatGPT-style)
      setTimeout(() => {
        try { inputRef.current?.focus(); } catch (_) { /* noop */ }
      }, 30);
    }
  };

  const regenerate = async () => {
    if (!convId || streaming) return;
    // find id of the last assistant (non-placeholder) message
    let removedId = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === "assistant" && !String(m.id).startsWith(STREAMING_MSG_PREFIX)) {
        removedId = m.id;
        break;
      }
    }
    if (!removedId) return;

    setStreaming(true);
    const placeholderId = `${STREAMING_MSG_PREFIX}${Date.now()}`;
    streamIdRef.current = placeholderId;

    setMessages((m) => {
      const kept = m.filter((x) => x.id !== removedId);
      return [...kept, {
        id: placeholderId,
        role: "assistant",
        content: "",
        created_at: new Date().toISOString(),
      }];
    });

    try {
      const { acc, finalAssistant, streamErr } = await runPostSSE(
        `${API_BASE}/chat/conversations/${convId}/regenerate`,
        null,
      );
      setMessages((m) => {
        const kept = m.filter((x) => x.id !== placeholderId);
        if (finalAssistant) return [...kept, finalAssistant];
        if (acc) return [...kept, {
          id: `local-${Date.now()}`,
          role: "assistant",
          content: acc,
          created_at: new Date().toISOString(),
        }];
        return kept;
      });
      if (streamErr) toast.error(streamErr);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Bağlantı hatası");
      setMessages((m) => m.filter((x) => x.id !== placeholderId));
    } finally {
      setStreaming(false);
      streamIdRef.current = null;
      abortRef.current = null;
    }
  };

  const stop = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const newChat = () => {
    if (streaming) stop();
    setConvId(null);
    setMessages([]);
    setParams({}, { replace: true });
  };

  // Mic tap handler — routes to the correct consent gate then to the
  // correct capture path. Native and server fallback each have their
  // own persistent consent (KVKK: audio leaving the device is a
  // materially different disclosure).
  const onMicTap = () => {
    if (!voice.supported) return;
    if (useServerFallback) {
      if (voiceServerConsent !== "granted" && voiceServerConsent !== "denied") {
        setVoiceServerConsentOpen(true);
        return;
      }
      if (voiceServerConsent === "denied") {
        toast.error("Sesli giriş kapalı. Profil > Sesli giriş izinleri'nden yeniden açabilirsin.");
        return;
      }
    } else {
      if (voiceConsent !== "granted" && voiceConsent !== "denied") {
        setVoiceConsentOpen(true);
        return;
      }
      if (voiceConsent === "denied") {
        toast.error("Sesli giriş kapalı. Profil > Sesli giriş izinleri'nden yeniden açabilirsin.");
        return;
      }
    }
    if (voice.status === "listening") {
      voice.stop();
      return;
    }
    setVoicePanelOpen(true);
    voice.start();
  };

  const acceptVoiceConsent = () => {
    try { localStorage.setItem(VOICE_CONSENT_KEY, "granted"); } catch { /* noop */ }
    setVoiceConsent("granted");
    setVoiceConsentOpen(false);
    setVoicePanelOpen(true);
    voice.start();
  };
  const declineVoiceConsent = () => {
    try { localStorage.setItem(VOICE_CONSENT_KEY, "denied"); } catch { /* noop */ }
    setVoiceConsent("denied");
    setVoiceConsentOpen(false);
  };
  const acceptVoiceServerConsent = () => {
    try { localStorage.setItem(VOICE_SERVER_CONSENT_KEY, "granted"); } catch { /* noop */ }
    setVoiceServerConsent("granted");
    setVoiceServerConsentOpen(false);
    setVoicePanelOpen(true);
    voice.start();
  };
  const declineVoiceServerConsent = () => {
    try { localStorage.setItem(VOICE_SERVER_CONSENT_KEY, "denied"); } catch { /* noop */ }
    setVoiceServerConsent("denied");
    setVoiceServerConsentOpen(false);
  };

  const commitVoiceTranscript = (text) => {
    if (!text) return;
    // Explicitly write into the existing composer state. The student must
    // still press the existing Send button. We never call send() here.
    setInput((prev) => {
      const glue = prev && !/\s$/.test(prev) ? " " : "";
      return (prev || "") + glue + text;
    });
    setVoicePanelOpen(false);
    voice.reset();
    try { inputRef.current?.focus(); } catch { /* noop */ }
  };
  const cancelVoicePanel = () => {
    voice.stop();
    voice.reset();
    setVoicePanelOpen(false);
  };

  // Show error toasts for permission-denied / unexpected errors, but only once
  // per transition (voice.error is set synchronously with status change).
  useEffect(() => {
    if (voice.error && (voice.status === "denied" || voice.status === "error")) {
      toast.error(voice.error);
    }
  }, [voice.error, voice.status]);

  // Consent revocation enforcement — Profile > Sesli giriş izinleri can
  // flip either flag to "denied" or clear it while a mic/upload/playback
  // session is active on this or another tab. React immediately by
  // stopping every voice surface we control:
  //   * native SpeechRecognition (stop + reset)
  //   * server-fallback MediaRecorder (stop + reset — this also aborts
  //     the in-flight upload state visually; the upload itself is
  //     still en route to the server but audio is not retained)
  //   * speechSynthesis playback (cancel)
  // localStorage is the single source of truth; we mirror it into state
  // so gate branches in onMicTap see the latest value without a reload.
  useEffect(() => {
    const apply = () => {
      const nextNative = (() => { try { return localStorage.getItem(VOICE_CONSENT_KEY) || ""; } catch { return ""; } })();
      const nextServer = (() => { try { return localStorage.getItem(VOICE_SERVER_CONSENT_KEY) || ""; } catch { return ""; } })();

      // Detect a transition to "denied" or empty ("reset") on the flag
      // powering the currently ACTIVE voice path — if so, tear down.
      const activeFlag = useServerFallback ? nextServer : nextNative;
      const noLongerGranted = activeFlag !== "granted";

      if (noLongerGranted) {
        try { nativeVoice.stop(); } catch { /* noop */ }
        try { serverVoice.stop(); } catch { /* noop */ }
        try { nativeVoice.reset(); } catch { /* noop */ }
        try { serverVoice.reset(); } catch { /* noop */ }
        try { playback.stop(); } catch { /* noop */ }
        setVoicePanelOpen(false);
      }
      setVoiceConsent(nextNative);
      setVoiceServerConsent(nextServer);
    };

    const onStorage = (e) => {
      if (!e) return;
      if (e.key === VOICE_CONSENT_KEY || e.key === VOICE_SERVER_CONSENT_KEY) apply();
    };
    const onLocal = () => apply();

    window.addEventListener("storage", onStorage);
    window.addEventListener("sz-voice-consent-change", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("sz-voice-consent-change", onLocal);
    };
    // Intentionally not depending on the hook instances — they are
    // stable per render and we want the effect wired exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useServerFallback]);

  return (
    <AppShell variant="student">
      <div className="h-[calc(100vh-56px)] lg:h-screen flex flex-col" data-testid="chat-page">
        <AiDisclosure />
        <VoiceConsentCard
          open={voiceConsentOpen}
          onAccept={acceptVoiceConsent}
          onDecline={declineVoiceConsent}
        />
        <VoiceServerConsentCard
          open={voiceServerConsentOpen}
          onAccept={acceptVoiceServerConsent}
          onDecline={declineVoiceServerConsent}
        />
        <div className="border-b border-zinc-200 bg-white px-4 lg:px-8 py-3 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#1E3A8A]" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-zinc-900 truncate">
                {convId ? "Sohbet" : "Yeni Sohbet"}
              </div>
              <div className="text-xs text-zinc-500 truncate">
                {grade ? labelForGrade(grade) : "Sınıf seç"} · {course || "Ders seç"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="h-9 w-[130px] rounded-full text-xs" data-testid="chat-grade">
                <SelectValue placeholder="Sınıf" />
              </SelectTrigger>
              <SelectContent>
                {GRADE_LEVELS.map((g) => (
                  <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={course} onValueChange={setCourse}>
              <SelectTrigger className="h-9 w-[150px] rounded-full text-xs" data-testid="chat-course">
                <SelectValue placeholder="Ders" />
              </SelectTrigger>
              <SelectContent>
                {COURSES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              className="h-9 rounded-full text-xs"
              onClick={newChat}
              data-testid="chat-new"
            >
              Yeni
            </Button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-zinc-50">
          <div className="max-w-3xl mx-auto px-4 lg:px-8 py-6 space-y-4">
            {loadingConv && (
              <div className="text-center text-sm text-zinc-500 py-10">
                <Loader2 className="w-4 h-4 animate-spin mx-auto" /> Yükleniyor…
              </div>
            )}
            {!loadingConv && messages.length === 0 && (
              <EmptyState grade={grade} course={course} onExample={setInput} />
            )}
            {messages.map((m, i) => {
              const isPlaceholder = String(m.id).startsWith(STREAMING_MSG_PREFIX);
              const isLastAssistant =
                m.role === "assistant" &&
                !isPlaceholder &&
                !messages.slice(i + 1).some((x) => x.role === "assistant" && !String(x.id).startsWith(STREAMING_MSG_PREFIX));
              return (
                <MessageBubble
                  key={m.id}
                  m={m}
                  isStreaming={isPlaceholder && streaming}
                  showRegenerate={isLastAssistant && !streaming && !!convId}
                  onRegenerate={regenerate}
                  playback={playback}
                />
              );
            })}
          </div>
        </div>

        <div className="border-t border-zinc-200 bg-white px-4 lg:px-8 py-4">
          <div className="max-w-3xl mx-auto">
            {(!grade || !course) && (
              <div className="mb-3 flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-lg">
                <AlertCircle className="w-3.5 h-3.5" />
                Başlamak için üstten sınıf ve ders seç.
              </div>
            )}
            {voicePanelOpen && (
              <VoicePreview
                listening={voice.status === "listening"}
                uploading={voice.status === "uploading"}
                interim={voice.interim}
                transcript={voice.transcript}
                onCommit={commitVoiceTranscript}
                onCancel={cancelVoicePanel}
              />
            )}
            <div className="rounded-2xl border border-zinc-200 bg-white focus-within:border-[#1E3A8A]/40 focus-within:ring-2 focus-within:ring-[#1E3A8A]/10 transition-all">
              <Textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Bir konu, soru ya da örnek yaz… (Enter = gönder, Shift+Enter = yeni satır)"
                rows={2}
                data-testid="chat-input"
                disabled={streaming}
                autoFocus
                spellCheck="false"
                autoComplete="off"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none min-h-[60px] max-h-[200px]"
              />
              <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-100">
                <div className="text-xs text-zinc-500">
                  {user?.plan === "premium" ? "Premium" : "Ücretsiz plan"} · Türkçe
                </div>
                {streaming ? (
                  <Button
                    onClick={stop}
                    variant="outline"
                    data-testid="chat-stop"
                    className="rounded-full h-9"
                  >
                    Durdur
                  </Button>
                ) : (
                  <div className="flex items-center gap-2">
                    <MicButton
                      status={voice.status}
                      supported={voice.supported}
                      errorMessage={voice.error}
                      onClick={onMicTap}
                      disabled={streaming}
                    />
                    <Button
                      onClick={send}
                      disabled={!input.trim() || !grade || !course}
                      data-testid="chat-send"
                      className="rounded-full h-9 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                    >
                      <Send className="w-3.5 h-3.5 mr-1" /> Gönder
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function MessageBubble({ m, isStreaming, showRegenerate, onRegenerate, playback }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(m.content || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* noop */
    }
  };

  // Strip markdown syntax so speechSynthesis doesn't read '#', '*', '`', etc.
  // aloud. Deliberately narrow — safe for the two-tier structure we emit
  // (body + optional H2 insight sections).
  const plainForSpeech = (m.content || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]*`/g, " ")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/_(.+?)_/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[>|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (m.role === "user") {
    return (
      <div className="flex justify-end" data-testid="msg-user">
        <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#1E3A8A] text-white px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap">
          {m.content}
        </div>
      </div>
    );
  }
  const empty = isStreaming && !m.content;
  const { body: assistantBody, insights } = splitAssistantContent(m.content || "");
  return (
    <div className="flex gap-3" data-testid={isStreaming ? "msg-streaming" : "msg-assistant"}>
      <div className="w-8 h-8 shrink-0 rounded-xl bg-white border border-zinc-200 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-[#1E3A8A]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="rounded-2xl rounded-tl-md bg-white border border-zinc-200 px-4 py-3">
          {empty ? (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <span className="sz-dot w-1.5 h-1.5 rounded-full bg-zinc-400" />
              <span className="sz-dot w-1.5 h-1.5 rounded-full bg-zinc-400" />
              <span className="sz-dot w-1.5 h-1.5 rounded-full bg-zinc-400" />
              <span>SuperZeka düşünüyor…</span>
            </div>
          ) : (
            <div className="prose-sz text-sm">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{assistantBody}</ReactMarkdown>
              {isStreaming && <span className="inline-block w-1.5 h-4 -mb-0.5 ml-0.5 bg-[#1E3A8A] animate-pulse" />}
            </div>
          )}
        </div>
        {!isStreaming && insights && (
          <InsightBands insights={insights} />
        )}
        {!isStreaming && m.content && (
          <div className="mt-2 pl-1 flex items-center gap-2 flex-wrap">
            <button
              onClick={copy}
              data-testid="chat-copy"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-zinc-200 text-xs text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-colors"
              aria-label="Yanıtı kopyala"
            >
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? "Kopyalandı" : "Kopyala"}
            </button>
            {playback && (
              <SpeakButton
                id={String(m.id)}
                text={plainForSpeech}
                supported={playback.supported}
                turkishAvailable={playback.turkishAvailable}
                speakingId={playback.speakingId}
                onSpeak={playback.speak}
                onStop={playback.stop}
              />
            )}
            {showRegenerate && (
              <button
                onClick={onRegenerate}
                data-testid="chat-regenerate"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-zinc-200 text-xs text-zinc-600 hover:text-zinc-900 hover:border-zinc-300 transition-colors"
              >
                <RotateCw className="w-3 h-3" />
                Yeniden üret
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ grade, course, onExample }) {
  const examples = [
    "Bu konunun mantığını basitçe anlatır mısın?",
    "Bu soruyu adım adım çözer misin?",
    "Bu formülü nerede kullanırım?",
    "Bir örnek verir misin?",
  ];
  return (
    <div className="text-center py-14" data-testid="chat-empty">
      <div className="w-14 h-14 rounded-2xl bg-white border border-zinc-200 flex items-center justify-center mx-auto">
        <Sparkles className="w-6 h-6 text-[#1E3A8A]" />
      </div>
      <h3 className="mt-5 font-display text-xl font-semibold text-zinc-900">
        Anlamadığın konuyu sor.
      </h3>
      <p className="mt-2 text-sm text-zinc-600 max-w-md mx-auto">
        {grade && course
          ? `${labelForGrade(grade)} · ${course} için sana yardım etmeye hazırım.`
          : "Önce sınıf ve dersini seç, sonra sorunu yaz."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
        {examples.map((e) => (
          <button
            key={e}
            onClick={() => onExample(e)}
            className="px-3 py-1.5 rounded-full bg-white border border-zinc-200 text-xs text-zinc-700 hover:border-zinc-300"
            data-testid={`example-${e.slice(0, 10)}`}
          >
            {e}
          </button>
        ))}
      </div>
    </div>
  );
}


/**
 * Renders the three-band pedagogical insight footer under an assistant reply.
 * Colours are muted so the answer stays hero; bands are optional (parser skips
 * empty ones).
 */
function InsightBands({ insights }) {
  const rows = [
    {
      key: "why",
      label: "Neden takıldın",
      body: insights.why,
      icon: HelpCircle,
      tone: "bg-amber-50 border-amber-200 text-amber-900",
      dot: "bg-amber-400",
      testid: "insight-why",
    },
    {
      key: "prereq",
      label: "Ön koşul",
      body: insights.prereq,
      icon: GraduationCap,
      tone: "bg-sky-50 border-sky-200 text-sky-900",
      dot: "bg-sky-400",
      testid: "insight-prereq",
    },
    {
      key: "next",
      label: "Sıradaki adım",
      body: insights.next,
      icon: ArrowRight,
      tone: "bg-emerald-50 border-emerald-200 text-emerald-900",
      dot: "bg-emerald-400",
      testid: "insight-next",
    },
  ].filter((r) => (r.body || "").trim().length > 0);

  if (rows.length === 0) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="insight-bands">
      <div className="pl-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
        SuperZeka özet
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          data-testid={r.testid}
          className={`flex gap-2.5 rounded-xl border px-3 py-2.5 ${r.tone}`}
        >
          <div className="pt-0.5">
            <r.icon className="w-4 h-4" />
          </div>
          <div className="text-sm leading-relaxed">
            <div className="text-[11px] font-semibold uppercase tracking-wider opacity-80">
              {r.label}
            </div>
            <div className="mt-0.5">{r.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
